import {
  TicketsService,
  TicketStatus,
  UpdateTicketRequest,
} from '@/@types/generated'
import { Breadcrumb } from '@/components/Atoms/Breadcrumbs'
import Loading from '@/components/Atoms/Loading'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Header } from '@/components/ui/header'
import { Label } from '@/components/ui/label'
import format from '@/lib/format'
import { CopyIcon, PlusIcon, TagIcon, XIcon } from 'lucide-react'
import React, { Suspense } from 'react'
import { Outlet } from 'react-router'
import { toast } from 'sonner'
import { TicketStatusBadge } from './TicketStatusBadge'
import { useTicket } from '../hooks/ticket'
import TicketTypesSelect from './TicketTypesSelect'
import { UserStampTooltip } from '@/components/Atoms/UserStampTooltip'
import { buildPath } from '@/routes'
import { TicketsFeature } from '@/lib/features/tickets'
import { hasFeature } from '@/components/Atoms/Permissions'
import { useAuth } from '@/hooks/useAuth'

export function TicketDetailsLayout() {
  // Hooks
  const { ticket, reload } = useTicket()
  const { user } = useAuth()

  // State
  const [isSaving, setIsSaving] = React.useState(false)

  // Update ticket helper
  const updateTicket = async (v: Partial<UpdateTicketRequest>) => {
    try {
      setIsSaving(true)
      await TicketsService.updateTicket(ticket.id!, v)
      toast.success('Ticket updated successfully')
      reload(true)
    } catch (error) {
      toast.error('Failed to update ticket. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }
  const canManage = hasFeature(TicketsFeature.Manage, user)

  return (
    <>
      <Breadcrumb
        label={ticket ? `#${ticket.code}` : '...'}
        path={buildPath('/tickets/:ticketId', { ticketId: ticket.id! })}
      />

      <div className="flex flex-col justify-between gap-4 md:flex-row">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <Header
            variant="h1"
            className="overflow-hidden overflow-ellipsis whitespace-nowrap"
          >
            {format.CarrierName(ticket.businessEntity)}
          </Header>
          <div className="flex items-center gap-2">
            <h2 className="text-md text-muted-foreground flex items-center font-mono tracking-wide">
              <span className="mr-1 select-none"># </span>
              {ticket.code}
              <Button
                variant="ghost"
                size="icon"
                aria-label="Copy ticket code"
                onClick={() => {
                  navigator.clipboard.writeText(ticket.code || '')
                  toast.success('Ticket code copied to clipboard')
                }}
              >
                <CopyIcon />
              </Button>
            </h2>
          </div>
        </div>
        <div className="flex flex-none flex-col items-start gap-1 lg:items-end">
          <div className="hidden grid-cols-[minmax(0,max-content)_1fr] gap-1 gap-x-2 text-right lg:grid">
            <Label className="text-muted-foreground justify-self-end text-sm">
              Filed On
            </Label>
            <UserStampTooltip by={ticket.created} />

            {ticket.responded && (
              <>
                <Label className="text-muted-foreground justify-self-end text-sm">
                  Responded
                </Label>
                <UserStampTooltip by={ticket.responded} />
              </>
            )}

            <Label className="text-muted-foreground justify-self-end text-sm">
              Last Updated
            </Label>
            <UserStampTooltip by={ticket.modified} />
          </div>
        </div>
      </div>

      {/* Mobile timestamps */}
      <div className="-mt-4 grid grid-flow-row grid-cols-2 grid-rows-3 gap-2 lg:hidden">
        <Label className="text-muted-foreground text-sm lg:text-right">
          Filed On
        </Label>
        <UserStampTooltip by={ticket.created} />

        {ticket.responded && (
          <>
            <Label className="text-muted-foreground text-sm lg:text-right">
              Responded
            </Label>
            <UserStampTooltip by={ticket.responded} />
          </>
        )}

        <Label className="text-muted-foreground text-sm lg:text-right">
          Last Updated
        </Label>
        <UserStampTooltip by={ticket.modified} />
      </div>

      {/* Ticket buttons */}
      <div className="-mt-2 flex flex-wrap items-start gap-2">
        <Badge variant="outline" className="h-8 text-sm">
          <TicketStatusBadge status={ticket.status} />
        </Badge>

        {ticket.types?.map((tt) => (
          <Badge
            key={tt.id}
            variant="outline"
            className="flex h-8 gap-1 text-sm"
          >
            <TagIcon className="mr-1 size-2" />
            {tt.name}
            {ticket.status !== TicketStatus.CLOSED && canManage && (
              <Button
                variant="ghost"
                size="xs"
                disabled={isSaving}
                className="flex h-5 w-5 items-center justify-center"
                onClick={() =>
                  updateTicket({
                    typeIds: ticket.types
                      ?.filter(({ id }) => id !== tt.id)
                      .map(({ id }) => id),
                  })
                }
              >
                <XIcon className="size-4" />
              </Button>
            )}
          </Badge>
        ))}

        {ticket.status !== TicketStatus.CLOSED && canManage && (
          <TicketTypesSelect
            department={ticket.department}
            value={ticket.types || []}
            disabled={isSaving}
            onChange={(v) => updateTicket({ typeIds: v?.map(({ id }) => id) })}
          >
            <Button size="sm">
              <PlusIcon className="size-4" />
              Ticket Type
            </Button>
          </TicketTypesSelect>
        )}
      </div>

      <Suspense fallback={<Loading variant="fullscreen" />}>
        <Outlet />
      </Suspense>
    </>
  )
}
